import { DASHBOARD_CONTEXT } from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { INDEX_PAGE_WITH_FILTERS } from '../IndexPage.constants'
import { FilterBarConfig } from './FilterBar.constants'

export type SearchBarOptions = {
  withContentSearch?: boolean
}

export type FilterBarProps = {
  config: FilterBarConfig
  page?: INDEX_PAGE_WITH_FILTERS
  resizeNodeId?: string
  searchBarOptions?: SearchBarOptions
  /** Renders inside the filter bar BEFORE the filters. Use for page-level controls
   *  that aren't filters themselves (e.g. an OE dashboard "Group by" dropdown). */
  leadingElement?: JSX.Element
  /** Renders AFTER the filter row, outside the wrapper. Use for trailing controls
   *  like Print/PDF buttons. */
  customElement?: JSX.Element
  dashboardContext?: DASHBOARD_CONTEXT
  dashboardName?: string
  /**
   * When true, expanded search + overflow shows a “See filters” control that collapses search.
   * When false/omitted, overflow uses the default “More filters” dropdown (unchanged).
   */
  useSeeFiltersWhenOverflow?: boolean
  /** When true, hides the Save/Load filters menu. Use in contexts where saved filters don't apply (e.g. chat panel). */
  disableSavedFilters?: boolean
}

export enum FilterParam {
  DEPARTMENT = 'department_ids',
  ACTIVITY_ACTOR = 'activity_actor',
  ACTIVITY_OBJECT_TYPE = 'activity_object_type',
  POLICY = 'policy_ids',
  STATUS = 'status',
  ACTIVE = 'active',
  EXCLUDE_NOT_APPLICABLE = 'exclude_not_applicable',
  OWNER = 'owner',
  ASSIGNEE = 'assignee',
  SERVICE = 'related_service_ids',
  HAS_DOCUMENTATION = 'has_documentation',
  IS_CUSTOMIZED = 'is_customized',
  AUDIT = 'audit',
  AUDIT_FRAMEWORKS = 'audit_frameworks',
  AUDIT_STATUS = 'audit_status',
  COMPLIANCE_FRAMEWORK = 'compliance_framework',
  INHERENT_RISK = 'inherent_risk',
  RESIDUAL_RISK = 'residual_risk',
  REVIEWED = 'reviewed',
  RISK_CATEGORY = 'risk_category',
  RECEPTOR = 'receptor_ids',
  SORT_ORDER = 'sort_order',
  SORT_COLUMN = 'sort_column',
  SERVICE_ROLE = 'service_roles',
  RISK_TIER = 'risk_tier',
  ATTESTATION = 'attestation_status',
  VENDOR_STATUS = 'vendor_status',
  VENDOR_STATE = 'vendor_state',
  VENDOR_EVALUATION = 'vendor_evaluation',
  AI_CONTROL_CHECKS = 'ai_control_checks',
  CONTROL = 'control_ids',
  ASSOCIATED_RISK = 'risk_ids',
  REQUEST = 'request_ids',
  SOURCE = 'sources',
  CREATED_SOURCE = 'created_source',
  FILE_TYPE = 'file_types',
  DATE_RANGE = 'date_range',
  PAGE = 'page',
  DOCUMENT_MANAGEMENT_SYSTEM = 'document_management_system',
  GRC_PLATFORM = 'grc_platform',
  EVIDENCE_TYPE = 'evidence_type',
  FOLDER_SCAN = 'folder_scan_ids',
  VISIBILITIES = 'visibilities',
  TAGS = 'tags',
  AUTOMATED = 'automated',
  TRUST_PORTAL_REQUEST_ACCESS = 'status',
  TRUST_PORTAL_PERMISSION = 'status',
  TRUST_PORTAL_FEATURES = 'features',
  ORGANIZATION = 'organization',
  QUESTIONNAIRE_STATUS = 'questionnaire_status',
  QUESTIONNAIRE_VENDOR = 'questionnaire_vendor',
  QUESTIONNAIRE_ASSIGNEE = 'questionnaire_assignee',
  AUDIT_RUN_OWNER = 'audit_run_owner',
  AUDIT_RUN_CONTROL = 'audit_run_control',
  AUDIT_RUN_STATUS = 'audit_run_status',
  AUDIT_RUN_RESULT = 'audit_run_result',
  COMPANY = 'company',
  REPORT_TYPE = 'report_type',
  TRUST_SERVICE_CRITERIA = 'trust_service_criteria',
  AUDITOR = 'auditor',
  EXPIRATION_DATE = 'expiration_date',
  USER_ROLE = 'user_role',
  ACCESS_ROLE = 'access_role',
  EMAIL_NOTIFICATION = 'email_notification',
  USER_DEPARTMENT = 'user_department',
  USER_STATUS = 'user_status',
  DASHBOARD_OWNER = 'dashboard_owner',
  DASHBOARD_AUDIT = 'dashboard_audit',
  DASHBOARD_RISK = 'dashboard_risk',
  DASHBOARD_DEPARTMENT = 'dashboard_department',
  DASHBOARD_FRAMEWORK = 'dashboard_framework',
  DASHBOARD_POLICY = 'dashboard_policy',
  DASHBOARD_DATE_RANGE = 'dashboard_date_range',
  DASHBOARD_CONTROL = 'dashboard_control',
  DASHBOARD_RISK_CATEGORY = 'dashboard_risk_category',
  DASHBOARD_VENDOR = 'dashboard_vendor',
  DASHBOARD_LABEL = 'dashboard_label',
  WORKFLOW_MANAGEMENT_SYSTEM = 'workflow_management_system',
  REPORT_PLAYBOOK = 'report_playbook',
  FRAMEWORK_TYPE = 'framework_type',
  NAME = 'name',
  DASHBOARD_NAME_DIMENSION = 'dashboard_name_dimension',
  DASHBOARD_EMAIL_DIMENSION = 'dashboard_email_dimension',
  DASHBOARD_COMPANY_DIMENSION = 'dashboard_company_dimension',
  DASHBOARD_TP_FEATURE_DIMENSION = 'dashboard_tp_feature_dimension',
  DASHBOARD_DOC_NAME_DIMENSION = 'dashboard_doc_name_dimension',
  DASHBOARD_TP_ACCESS_LEVEL_DIMENSION = 'dashboard_tp_access_level_dimension',
  DASHBOARD_TP_UPDATE_CATEGORY_DIMENSION = 'dashboard_tp_update_category_dimension',
  EXECUTION_TYPE = 'execution_type',
  PLAYBOOK_TYPE = 'playbook_type',
  PLAYBOOK_CATEGORY = 'playbook_category',
  ARTIFACT_TYPE = 'artifact_types',
  REQUEST_USE_CASE_TYPE = 'use_case_type',
  REQUEST_RFE = 'rfe',
  REQUEST_RFI = 'rfi',
  REQUEST_RFA = 'rfa',
  REQUEST_RFVA = 'rfva',
  REQUEST_RFP = 'rfp',
  REQUEST_CONTROLS = 'req_controls',
  REQUEST_EVIDENCE = 'req_evidence',
  REQUEST_POLICY = 'req_policy',
  REQUEST_VENDOR = 'req_vendor',
  REQUEST_RISK = 'req_risk',
  REQUEST_SOURCE = 'request_source',
  LABEL = 'label_ids',
  SECTION = 'section_ids',
  ACCOUNT_TYPE = 'account_type',
  TRUSTERO_USER = 'trustero_user_emails',
  CITATION_POLICY = 'citation_policy',
  CITATION_CONTROL = 'citation_control',
  CITATION_EVIDENCE = 'citation_evidence',
  CITATION_REQUEST = 'citation_request',
  CITATION_RISK = 'citation_risk',
  CITATION_TYPES = 'citation_types',
}

export enum FilterName {
  DEPARTMENT = 'Department',
  ACTIVITY_ACTOR = 'Owner',
  ACTIVITY_OBJECT_TYPE = 'Object Type',
  POLICY = 'Policy',
  STATUS = 'Status',
  ACTIVE = 'Active',
  RECEPTOR_STATUS = 'Receptor Status',
  OWNER = 'Owner',
  ASSIGNEE = 'Assignee',
  SERVICE = 'Service',
  HAS_DOCUMENTATION = 'Documentation',
  IS_CUSTOMIZED = 'Customized',
  AUDIT = 'Audit',
  AUDIT_FRAMEWORKS = 'Framework',
  AUDIT_STATUS = 'Status',
  COMPLIANCE_FRAMEWORK = 'Framework',
  INHERENT_RISK = 'Inherent Risk',
  RESIDUAL_RISK = 'Residual Risk',
  REVIEWED = 'Reviewed',
  RISK_CATEGORY = 'Risk Category',
  RECEPTOR = 'Receptor',
  SERVICE_ROLE = 'Service Role',
  RISK_TIER = 'Risk Tier',
  ATTESTATION = 'Attestation',
  VENDOR_STATUS = 'Status',
  VENDOR_STATE = 'State',
  VENDOR_EVALUATION = 'Evaluation',
  AI_CONTROL_CHECKS = 'Control Tests',
  SOURCE = 'Source',
  FILE_TYPE = 'File Type',
  CONTROL = 'Linked Control',
  EVIDENCE_TYPE = 'Type',
  FOLDER_SCAN = 'Folder Scan',
  VISIBILITIES = 'Visibilities',
  TAGS = 'Tags',
  FRAMEWORK_TYPE = 'Type',
  NAME = 'Name',
  TRUST_PORTAL_REQUEST_ACCESS = 'Request Status',
  TRUST_PORTAL_PERMISSION = 'Status',
  TRUST_PORTAL_FEATURES = 'Access Feature',
  ORGANIZATION = 'Organization',
  QUESTIONNAIRE_STATUS = 'Status',
  QUESTIONNAIRE_VENDOR = 'Vendor',
  QUESTIONNAIRE_ASSIGNEE = 'Assignee',
  AUDIT_RUN_OWNER = 'Owner',
  AUDIT_RUN_CONTROL = 'Control',
  AUDIT_RUN_STATUS = 'Status',
  AUDIT_RUN_RESULT = 'Result',
  COMPANY = 'Company',
  REPORT_TYPE = 'Report Type',
  TRUST_SERVICE_CRITERIA = 'Trust Service Criteria',
  AUDITOR = 'Auditor',
  EXPIRATION_DATE = 'Expiration Date',
  USER_ROLE = 'User Role',
  EMAIL_NOTIFICATION = 'Email Notification',
  USER_DEPARTMENT = 'Department',
  USER_STATUS = 'Status',
  DASHBOARD_OWNER = 'Owner',
  DASHBOARD_AUDIT = 'Audit',
  DASHBOARD_RISK = 'Risk',
  DASHBOARD_DEPARTMENT = 'Department',
  DASHBOARD_FRAMEWORK = 'Framework',
  DASHBOARD_POLICY = 'Policy',
  DASHBOARD_DATE_RANGE = 'Date Range',
  DASHBOARD_CONTROL = 'Control',
  DASHBOARD_RISK_CATEGORY = 'Category',
  DASHBOARD_VENDOR = 'Vendor',
  DASHBOARD_LABEL = 'Label',
  REPORT_PLAYBOOK = 'Playbook',
  DASHBOARD_NAME_DIMENSION = 'Name',
  DASHBOARD_EMAIL_DIMENSION = 'Email',
  DASHBOARD_COMPANY_DIMENSION = 'Company',
  DASHBOARD_TP_FEATURE_DIMENSION = 'Feature',
  DASHBOARD_DOC_NAME_DIMENSION = 'Document Name',
  DASHBOARD_TP_ACCESS_LEVEL_DIMENSION = 'Access Level',
  DASHBOARD_TP_UPDATE_CATEGORY_DIMENSION = 'Category',
  EXECUTION_TYPE = 'Job Execution Type',
  // Access roles are presented to users as "user roles"; the legacy USER_ROLE enum
  // this replaces is on its way out.
  ACCESS_ROLE = 'User Role',
  PLAYBOOK_TYPE = 'Playbooks',
  PLAYBOOK_CATEGORY = 'Category',
  REQUEST_USE_CASE_TYPE = 'Type',
  REQUEST_SOURCE = 'Source',
  LABEL = 'Label',
  SECTION = 'Section',
  ACCOUNT_TYPE = 'Account Type',
  TRUSTERO_USER = 'Trustero User',
  CITATION_POLICY = 'Policy Citation',
  CITATION_CONTROL = 'Control Citation',
  CITATION_EVIDENCE = 'Evidence Citation',
  CITATION_REQUEST = 'Request Citation',
  CITATION_RISK = 'Risk Citation',
  CITATION_TYPES = 'Other Citation Types',
  ASSOCIATED_RISK = 'Associated Risk',
}
